// Day 8 - Full Production Content (PREMIUM)
// Temat: Budowanie promptów wielowarstwowych i sterowanie jakością odpowiedzi AI
// Ton: zaawansowany, praktyczny, premium, profesjonalny
// Duration: 15-20 minutes

export const day8Content = {
  day_number: 8,
  title: "Prompty wielowarstwowe",
  subtitle: "Sterowanie jakością odpowiedzi AI jak profesjonalista",
  duration: "15-20 min",
  is_premium: true,
  has_ai_coach: true,
  exercises: [
    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 1: Warstwy instrukcji (6 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d8_ex1",
      title: "Anatomia promptu wielowarstwowego",
      icon: "layers",
      steps: [
        {
          id: "d8_ex1_s1",
          type: "choice",
          title: "Dlaczego warstwy?",
          content: "Czym prompt wielowarstwowy różni się od zwykłego polecenia?",
          options: [
            { id: "a", text: "Jest dłuższy i przez to lepszy", correct: false },
            { id: "b", text: "Każda warstwa kontroluje inny aspekt odpowiedzi: rolę, cel, format, ton, ograniczenia", correct: true },
            { id: "c", text: "Używa więcej słów kluczowych", correct: false },
            { id: "d", text: "Nie ma żadnej różnicy, liczy się tylko temat", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl o promptcie jak o wielopiętrowym budynku — każde piętro ma swoją funkcję.",
          successMessage: "Dokładnie! Prompt wielowarstwowy to precyzyjne sterowanie wieloma wymiarami odpowiedzi jednocześnie."
        },
        {
          id: "d8_ex1_s2",
          type: "reorder",
          title: "Kolejność warstw",
          content: "Ułóż warstwy profesjonalnego promptu w zalecanej kolejności:",
          items: [
            { id: "r1", text: "Rola i perspektywa", order: 1 },
            { id: "r2", text: "Cel i zadanie główne", order: 2 },
            { id: "r3", text: "Kontekst i dane wejściowe", order: 3 },
            { id: "r4", text: "Format i struktura odpowiedzi", order: 4 },
            { id: "r5", text: "Ton, styl i ograniczenia", order: 5 },
            { id: "r6", text: "Kryteria jakości i walidacji", order: 6 }
          ],
          softHint: "Od tego KIM jest AI, przez CO ma zrobić, do tego JAK ma to wyglądać.",
          successMessage: "To jest profesjonalna kolejność warstw! Każda buduje na poprzedniej."
        },
        {
          id: "d8_ex1_s3",
          type: "classify",
          title: "Rozpoznaj warstwy",
          content: "Przyporządkuj każdy fragment promptu do odpowiedniej warstwy:",
          categories: ["Rola", "Format", "Ograniczenia"],
          items: [
            { id: "c1", text: "Jesteś doświadczonym strategiem marketingowym", category: "Rola" },
            { id: "c2", text: "Odpowiedz w formie tabeli z 4 kolumnami", category: "Format" },
            { id: "c3", text: "Nie używaj żargonu technicznego", category: "Ograniczenia" },
            { id: "c4", text: "Działasz jako konsultant z 15-letnim stażem", category: "Rola" },
            { id: "c5", text: "Każdy punkt max 2 zdania, użyj nagłówków H2", category: "Format" },
            { id: "c6", text: "Pomiń oczywiste rady, skup się na nieoczywistych insight-ach", category: "Ograniczenia" }
          ],
          softHint: "Rola = kim jest AI. Format = jak ma wyglądać odpowiedź. Ograniczenia = czego unikać.",
          successMessage: "Rozpoznajesz warstwy! To klucz do pisania profesjonalnych promptów."
        },
        {
          id: "d8_ex1_s4",
          type: "choice",
          title: "Siła warstwy ograniczeń",
          content: "Który prompt lepiej kontroluje jakość odpowiedzi?",
          options: [
            { id: "a", text: "Napisz mi artykuł o produktywności", correct: false },
            { id: "b", text: "Napisz mi artykuł o produktywności. Nie powtarzaj ogólnikowych rad typu 'wstawaj wcześnie'. Każda rada musi zawierać konkretny przykład wdrożenia. Max 800 słów.", correct: true },
            { id: "c", text: "Napisz mi długi, szczegółowy artykuł o produktywności z wieloma poradami", correct: false },
            { id: "d", text: "Napisz coś ciekawego o produktywności, bądź kreatywny", correct: false }
          ],
          shuffle: true,
          softHint: "Im precyzyjniej powiesz AI czego NIE chcesz, tym lepszy wynik dostaniesz.",
          successMessage: "Tak! Ograniczenia eliminują generyczne odpowiedzi i wymuszają konkretność."
        },
        {
          id: "d8_ex1_s5",
          type: "shortPrompt",
          title: "Zbuduj prompt wielowarstwowy",
          content: "Napisz prompt wielowarstwowy do stworzenia planu treningowego. Użyj minimum 3 warstw: roli, formatu i ograniczeń.",
          placeholder: "Jesteś doświadczonym trenerem personalnym...",
          minLength: 120,
          requiredElements: ["jeste|działaj|wciel|perspektyw", "format|struktur|tabela|lista|nagłówk", "nie |unikaj|pomiń|bez |max |minimum"],
          softHint: "Zacznij od roli, określ format odpowiedzi, dodaj ograniczenia jakościowe.",
          successMessage: "Świetny prompt wielowarstwowy! Widzisz, jak warstwy współpracują ze sobą?"
        },
        {
          id: "d8_ex1_s6",
          type: "choice",
          title: "Ile warstw to za dużo?",
          content: "Kiedy prompt wielowarstwowy zaczyna tracić skuteczność?",
          options: [
            { id: "a", text: "Już przy 2 warstwach — AI się gubi", correct: false },
            { id: "b", text: "Gdy warstwy są sprzeczne lub gdy jest ich tak dużo, że AI nie może spełnić wszystkich naraz", correct: true },
            { id: "c", text: "Nigdy — im więcej warstw, tym lepiej", correct: false },
            { id: "d", text: "Przy więcej niż 3 warstwach", correct: false }
          ],
          shuffle: true,
          softHint: "Nie chodzi o liczbę warstw, lecz o ich spójność i wykonalność.",
          successMessage: "Właśnie! Spójność warstw jest ważniejsza niż ich liczba. Jakość ponad ilość."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 2: Sterowanie tonem i stylem (5 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d8_ex2",
      title: "Precyzyjne sterowanie tonem",
      icon: "sliders",
      steps: [
        {
          id: "d8_ex2_s1",
          type: "choice",
          title: "Ton to nie to samo co temat",
          content: "Ten sam temat, inny ton — który prompt da najbardziej profesjonalny wynik?",
          options: [
            { id: "a", text: "Napisz o korzyściach medytacji", correct: false },
            { id: "b", text: "Napisz o korzyściach medytacji w tonie eksperta neurobiologii, z odwołaniami do badań, bez ezoterycznego języka", correct: true },
            { id: "c", text: "Napisz fajnie o medytacji", correct: false },
            { id: "d", text: "Napisz długo i szczegółowo o medytacji", correct: false }
          ],
          shuffle: true,
          softHint: "Ton eksperta + konkretna dyscyplina + wykluczenie niechcianych elementów.",
          successMessage: "Tak! Definiowanie tonu przez pryzmat konkretnej perspektywy daje precyzyjne wyniki."
        },
        {
          id: "d8_ex2_s2",
          type: "classify",
          title: "Dopasuj ton do odbiorcy",
          content: "Przyporządkuj instrukcję tonu do właściwego odbiorcy:",
          categories: ["Zarząd firmy", "Zespół techniczny", "Klient końcowy"],
          items: [
            { id: "c1", text: "Ton: formalny, zwięzły, dane w punktach, focus na ROI", category: "Zarząd firmy" },
            { id: "c2", text: "Ton: techniczny, precyzyjny, z konkretnymi specyfikacjami", category: "Zespół techniczny" },
            { id: "c3", text: "Ton: przystępny, ciepły, bez żargonu, z korzyściami", category: "Klient końcowy" },
            { id: "c4", text: "Ton: strategiczny, z metrykami biznesowymi i projekcjami", category: "Zarząd firmy" },
            { id: "c5", text: "Ton: instruktażowy, krok po kroku, z przykładami kodu", category: "Zespół techniczny" },
            { id: "c6", text: "Ton: konwersacyjny, empatyczny, z FAQ", category: "Klient końcowy" }
          ],
          softHint: "Zarząd chce liczb i strategii. Techniczni — szczegółów. Klient — korzyści i prostoty.",
          successMessage: "Dopasowanie tonu do odbiorcy to jedna z najcenniejszych umiejętności promptowania!"
        },
        {
          id: "d8_ex2_s3",
          type: "shortPrompt",
          title: "Transformacja tonu",
          content: "Napisz prompt, który poprosi AI o wyjaśnienie tego samego tematu (np. blockchain) w dwóch wersjach: dla dyrektora finansowego i dla 15-latka.",
          placeholder: "Wyjaśnij czym jest blockchain w dwóch wersjach...",
          minLength: 80,
          requiredElements: ["dyrekt|CFO|finans|zarząd|prezes", "15-lat|nastolet|młod|uczeń|dziec", "blockchain|technolog"],
          softHint: "Zdefiniuj obie grupy docelowe, ich język, ich potrzeby informacyjne.",
          successMessage: "Doskonale! Umiejętność przełączania tonu to cecha profesjonalnego prompt engineera."
        },
        {
          id: "d8_ex2_s4",
          type: "choice",
          title: "Kontrola długości i gęstości",
          content: "Jak najlepiej kontrolować długość i gęstość informacji w odpowiedzi?",
          options: [
            { id: "a", text: "Napisz krótko", correct: false },
            { id: "b", text: "Max 200 słów. Każde zdanie musi wnosić nową informację. Bez wstępów i podsumowań.", correct: true },
            { id: "c", text: "Bądź zwięzły", correct: false },
            { id: "d", text: "Nie pisz za dużo", correct: false }
          ],
          shuffle: true,
          softHint: "Precyzyjne liczby + zasady eliminacji zbędnych elementów.",
          successMessage: "Dokładnie! Konkretny limit + reguły gęstości = zwarta, wartościowa odpowiedź."
        },
        {
          id: "d8_ex2_s5",
          type: "fill",
          title: "Złota zasada tonu",
          content: "Dokończ regułę:",
          template: "Nie mów AI 'pisz profesjonalnie' — powiedz AI, dla _______ pisze i jaki efekt ma osiągnąć.",
          correctAnswers: ["kogo", "jakiego odbiorcy", "jakiej osoby", "kogo konkretnie", "jakiego czytelnika"],
          minLength: 3,
          softHint: "Profesjonalizm zależy od perspektywy odbiorcy.",
          successMessage: "Tak! Odbiorca definiuje ton, nie odwrotnie."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 3: Wymuszanie struktury odpowiedzi (6 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d8_ex3",
      title: "Wymuszanie precyzyjnej struktury",
      icon: "layout",
      steps: [
        {
          id: "d8_ex3_s1",
          type: "choice",
          title: "Dlaczego struktura?",
          content: "Co daje wymuszenie struktury odpowiedzi w prompcie?",
          options: [
            { id: "a", text: "Nic — AI i tak odpowie jak chce", correct: false },
            { id: "b", text: "Przewidywalny format, łatwiejsze porównywanie wyników, gotowość do użycia bez edycji", correct: true },
            { id: "c", text: "Tylko estetykę", correct: false },
            { id: "d", text: "Dłuższą odpowiedź", correct: false }
          ],
          shuffle: true,
          softHint: "Strukturyzowane odpowiedzi = mniej pracy po stronie użytkownika.",
          successMessage: "Dokładnie! Struktura oznacza, że wynik jest od razu użyteczny, nie wymaga przerabiania."
        },
        {
          id: "d8_ex3_s2",
          type: "choice",
          title: "Techniki wymuszania struktury",
          content: "Która technika najskuteczniej wymusza konkretny format?",
          options: [
            { id: "a", text: "Napisz ładnie i przejrzyście", correct: false },
            { id: "b", text: "Odpowiedz w formacie: [Nagłówek] → [Opis 2 zdania] → [Przykład] → [Pułapka do uniknięcia]. Powtórz dla każdego punktu.", correct: true },
            { id: "c", text: "Użyj punktów", correct: false },
            { id: "d", text: "Sformatuj to dobrze", correct: false }
          ],
          shuffle: true,
          softHint: "Podaj AI szablon odpowiedzi z nazwanymi sekcjami — powtórzy go wiernie.",
          successMessage: "Tak! Szablon z nazwanymi sekcjami to najpotężniejsza technika kontroli formatu."
        },
        {
          id: "d8_ex3_s3",
          type: "reorder",
          title: "Szablon analizy SWOT",
          content: "Ułóż elementy szablonu, który wymusisz w prompcie do analizy SWOT:",
          items: [
            { id: "r1", text: "Kontekst firmy (1-2 zdania)", order: 1 },
            { id: "r2", text: "Strengths — 3 punkty z przykładami", order: 2 },
            { id: "r3", text: "Weaknesses — 3 punkty z planem naprawy", order: 3 },
            { id: "r4", text: "Opportunities — 3 punkty z timeline", order: 4 },
            { id: "r5", text: "Threats — 3 punkty z mitygacją ryzyka", order: 5 },
            { id: "r6", text: "Podsumowanie strategiczne (3 zdania)", order: 6 }
          ],
          softHint: "Klasyczny SWOT, ale z dodatkową wartością w każdej sekcji.",
          successMessage: "Widzisz różnicę? Każda sekcja ma dodatkowe wymaganie, które podnosi jakość analizy."
        },
        {
          id: "d8_ex3_s4",
          type: "shortPrompt",
          title: "Zbuduj szablon odpowiedzi",
          content: "Napisz prompt, w którym poprosisz AI o recenzję książki, wymuszając konkretny szablon odpowiedzi z minimum 4 nazwanymi sekcjami.",
          placeholder: "Napisz recenzję książki '[TYTUŁ]' w następującym formacie...",
          minLength: 100,
          requiredElements: ["format|szablon|struktur|sekcj", "ocena|rating|gwiazdki|nota|punkt", "dla kogo|odbiorca|czytelnik|polecam"],
          softHint: "Podaj sekcje z nazwami: np. Temat, Mocne strony, Słabe strony, Ocena, Dla kogo.",
          successMessage: "Profesjonalny szablon! AI będzie trzymać się tej struktury, dając spójny, użyteczny wynik."
        },
        {
          id: "d8_ex3_s5",
          type: "choice",
          title: "Tabele vs tekst",
          content: "Kiedy warto wymusić format tabelaryczny zamiast tekstu?",
          options: [
            { id: "a", text: "Zawsze — tabele są lepsze", correct: false },
            { id: "b", text: "Gdy porównujesz opcje, prezentujesz dane wielowymiarowe lub potrzebujesz szybkiego przeglądu", correct: true },
            { id: "c", text: "Nigdy — AI nie radzi sobie z tabelami", correct: false },
            { id: "d", text: "Tylko przy liczbach", correct: false }
          ],
          shuffle: true,
          softHint: "Tabele = porównania, dane, przegląd. Tekst = narracja, wyjaśnienia, argumentacja.",
          successMessage: "Tak! Dopasuj format do celu — tabela do porównań, tekst do narracji."
        },
        {
          id: "d8_ex3_s6",
          type: "fill",
          title: "Kluczowa technika",
          content: "Dokończ zdanie:",
          template: "Zamiast prosić AI o 'dobrą odpowiedź', podaj mu _______ odpowiedzi, którą ma wypełnić treścią.",
          correctAnswers: ["szablon", "strukturę", "format", "wzór", "schemat", "template", "ramę"],
          minLength: 4,
          softHint: "Daj AI gotowy szkielet do wypełnienia.",
          successMessage: "Dokładnie! Szablon = powtarzalna jakość."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 4: Iteracja i poprawianie promptów (6 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d8_ex4",
      title: "Iteracja: od słabego do doskonałego",
      icon: "refresh-cw",
      steps: [
        {
          id: "d8_ex4_s1",
          type: "choice",
          title: "Prompt przeciętny vs profesjonalny",
          content: "Który z tych promptów da lepszy opis produktu?",
          options: [
            { id: "a", text: "Napisz opis słuchawek bezprzewodowych", correct: false },
            { id: "b", text: "Opisz te słuchawki do opisu fajnego", correct: false },
            { id: "c", text: "Jesteś copywriterem premium marek audio. Napisz opis słuchawek XYZ dla audiofili 25-40 lat. Format: nagłówek emocjonalny + 3 sekcje po 2 zdania (dźwięk, komfort, design). Ton: elegancki, bez superlatyw. Max 120 słów.", correct: true },
            { id: "d", text: "Napisz długi, szczegółowy i profesjonalny opis słuchawek", correct: false }
          ],
          shuffle: true,
          softHint: "Profesjonalny prompt definiuje: kto pisze, dla kogo, w jakim formacie, w jakim tonie, ile.",
          successMessage: "To jest różnica między promptem za 0 zł a promptem za 500 zł. Precyzja na każdej warstwie."
        },
        {
          id: "d8_ex4_s2",
          type: "reorder",
          title: "Proces iteracji promptu",
          content: "Ułóż etapy iteracyjnego ulepszania promptu:",
          items: [
            { id: "r1", text: "Napisz pierwszą wersję promptu", order: 1 },
            { id: "r2", text: "Przeczytaj odpowiedź AI i zidentyfikuj słabe punkty", order: 2 },
            { id: "r3", text: "Dodaj brakujące warstwy lub ograniczenia", order: 3 },
            { id: "r4", text: "Przetestuj poprawioną wersję", order: 4 },
            { id: "r5", text: "Powtórz aż wynik spełni oczekiwania", order: 5 }
          ],
          softHint: "Pisz → Testuj → Analizuj → Poprawiaj → Powtórz.",
          successMessage: "To jest profesjonalny workflow! Najlepsze prompty powstają przez iterację, nie za pierwszym razem."
        },
        {
          id: "d8_ex4_s3",
          type: "choice",
          title: "Diagnoza słabej odpowiedzi",
          content: "AI dał zbyt ogólną odpowiedź. Co najprawdopodobniej brakuje w Twoim prompcie?",
          options: [
            { id: "a", text: "Prompt jest za krótki", correct: false },
            { id: "b", text: "Brakuje konkretnych ograniczeń, kontekstu odbiorcy i przykładu pożądanego wyniku", correct: true },
            { id: "c", text: "AI jest za słabe na ten temat", correct: false },
            { id: "d", text: "Trzeba użyć innego AI", correct: false }
          ],
          shuffle: true,
          softHint: "Ogólna odpowiedź = ogólny prompt. Dodaj ograniczenia i kontekst.",
          successMessage: "Tak! Generyczna odpowiedź to sygnał, że prompt potrzebuje więcej warstw precyzji."
        },
        {
          id: "d8_ex4_s4",
          type: "shortPrompt",
          title: "Popraw słaby prompt",
          content: "Oto słaby prompt: 'Napisz mi biznesplan'. Przepisz go jako prompt wielowarstwowy z minimum 4 warstwami (rola, cel, format, ograniczenia).",
          placeholder: "Jesteś doświadczonym konsultantem strategicznym...",
          minLength: 140,
          requiredElements: ["jeste|działaj|perspektyw|konsultant", "biznesplan|plan biznes|strategia biznes", "format|struktur|sekcj|rozdział", "nie |unikaj|pomiń|bez |max |konkret"],
          softHint: "Rola + szczegółowy cel + szablon formatu + ograniczenia jakościowe.",
          successMessage: "Z jednozdaniowego polecenia do wielowarstwowego promptu! Widzisz skok jakości?"
        },
        {
          id: "d8_ex4_s5",
          type: "classify",
          title: "Diagnozuj problem w odpowiedzi",
          content: "AI dał słabą odpowiedź. Przyporządkuj symptom do brakującej warstwy:",
          categories: ["Brak roli", "Brak ograniczeń", "Brak formatu"],
          items: [
            { id: "c1", text: "Odpowiedź brzmi jak z Wikipedii", category: "Brak roli" },
            { id: "c2", text: "Odpowiedź jest rozwlekła i pełna banałów", category: "Brak ograniczeń" },
            { id: "c3", text: "Odpowiedź to ściana tekstu bez struktury", category: "Brak formatu" },
            { id: "c4", text: "Ton jest szkolny zamiast eksperckiego", category: "Brak roli" },
            { id: "c5", text: "Odpowiedź zawiera oczywistości zamiast insight-ów", category: "Brak ograniczeń" },
            { id: "c6", text: "Trudno znaleźć kluczowe informacje", category: "Brak formatu" }
          ],
          softHint: "Brak roli = zły ton. Brak ograniczeń = za dużo bylejakości. Brak formatu = chaos.",
          successMessage: "Teraz potrafisz diagnozować problemy i wiedzieć, którą warstwę dodać!"
        },
        {
          id: "d8_ex4_s6",
          type: "choice",
          title: "Technika 'prompt chirurgiczny'",
          content: "Jak najskuteczniej poprawić prompt, który daje wynik 'prawie dobry'?",
          options: [
            { id: "a", text: "Napisać prompt od nowa", correct: false },
            { id: "b", text: "Dodać precyzyjne ograniczenie eliminujące konkretny problem, np. 'Pomiń wstęp, zacznij od sedna'", correct: true },
            { id: "c", text: "Poprosić AI, żeby się bardziej postarało", correct: false },
            { id: "d", text: "Zmienić model AI na droższy", correct: false }
          ],
          shuffle: true,
          softHint: "Chirurgiczna precyzja: jeden cel, jedna poprawka, jeden test.",
          successMessage: "Dokładnie! Nie przepisuj wszystkiego. Dodaj jedno celne ograniczenie i przetestuj."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 5: Masterclass — pełna budowa (5 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "d8_ex5",
      title: "Masterclass: profesjonalny prompt od zera",
      icon: "trophy",
      steps: [
        {
          id: "d8_ex5_s1",
          type: "choice",
          title: "Sprawdzian: rozpoznaj poziom",
          content: "Który prompt reprezentuje poziom profesjonalny?",
          options: [
            { id: "a", text: "Zrób mi prezentację o sprzedaży", correct: false },
            { id: "b", text: "Zrób dobrą prezentację o sprzedaży, bądź kreatywny i profesjonalny", correct: false },
            { id: "c", text: "Jesteś dyrektorem sprzedaży B2B SaaS z 10-letnim doświadczeniem. Przygotuj strukturę 12-slajdowej prezentacji kwartalnych wyników. Format: Slajd [numer] — [Tytuł] — [3 bullet pointy treści] — [Sugerowana grafika]. Ton: pewny, oparty na danych. Pomiń slajdy 'Dziękuję' i 'Agenda'.", correct: true },
            { id: "d", text: "Napisz długą prezentację sprzedażową ze wszystkimi szczegółami", correct: false }
          ],
          shuffle: true,
          softHint: "Rola + konkretne zadanie + precyzyjny szablon + ton + ograniczenia = profesjonalizm.",
          successMessage: "To jest prompt na poziomie seniorskim! Każda warstwa pracuje na jakość końcowego wyniku."
        },
        {
          id: "d8_ex5_s2",
          type: "shortPrompt",
          title: "Twój najlepszy prompt",
          content: "Napisz profesjonalny, wielowarstwowy prompt do stworzenia oferty handlowej dla klienta B2B. Użyj wszystkich poznanych technik: rola, kontekst, format z szablonem, ton, ograniczenia.",
          placeholder: "Jesteś senior account managerem w firmie technologicznej...",
          minLength: 160,
          requiredElements: ["jeste|działaj|perspektyw", "ofert|propozycj|handlow", "format|struktur|sekcj|szablon", "ton|styl|język", "nie |unikaj|bez |max |pomiń|konkret"],
          softHint: "Użyj wszystkich 5 warstw: rola, cel, kontekst, format, ograniczenia. To Twój masterpiece!",
          successMessage: "Imponujący prompt! To jest poziom profesjonalisty. Porównaj to z tym, jak pisałeś prompty na początku kursu."
        },
        {
          id: "d8_ex5_s3",
          type: "choice",
          title: "Kiedy NOT to do?",
          content: "W jakiej sytuacji NIE potrzebujesz promptu wielowarstwowego?",
          options: [
            { id: "a", text: "Nigdy — zawsze warto", correct: false },
            { id: "b", text: "Przy prostych, jednorazowych pytaniach typu 'Ile mieszkańców ma Warszawa?'", correct: true },
            { id: "c", text: "Przy ważnych zadaniach", correct: false },
            { id: "d", text: "Gdy AI jest wystarczająco inteligentne", correct: false }
          ],
          shuffle: true,
          softHint: "Proste pytanie = prosta odpowiedź. Wielowarstwowość to narzędzie, nie obowiązek.",
          successMessage: "Właśnie! Stosuj wielowarstwowość tam, gdzie robi różnicę. Nie komplikuj prostych rzeczy."
        },
        {
          id: "d8_ex5_s4",
          type: "reorder",
          title: "Twoja checklista promptu",
          content: "Ułóż checklistę, którą przejdziesz przed wysłaniem ważnego promptu:",
          items: [
            { id: "r1", text: "Czy zdefiniowałem rolę / perspektywę AI?", order: 1 },
            { id: "r2", text: "Czy cel jest jednoznaczny i konkretny?", order: 2 },
            { id: "r3", text: "Czy podałem wystarczający kontekst?", order: 3 },
            { id: "r4", text: "Czy określiłem format i strukturę odpowiedzi?", order: 4 },
            { id: "r5", text: "Czy dodałem ograniczenia eliminujące słabe wyniki?", order: 5 }
          ],
          softHint: "Rola → Cel → Kontekst → Format → Ograniczenia.",
          successMessage: "To jest Twoja profesjonalna checklista! Używaj jej przy każdym ważnym prompcie."
        },
        {
          id: "d8_ex5_s5",
          type: "choice",
          title: "Podsumowanie dnia 8",
          content: "Dzisiaj nauczyłeś się sterować AI na poziomie profesjonalnym. Kluczowa zasada to:",
          options: [
            { id: "a", text: "Pisz jak najdłuższe prompty", correct: false },
            { id: "b", text: "Używaj każdej warstwy świadomie — rola, cel, kontekst, format, ton, ograniczenia — żeby kontrolować każdy aspekt odpowiedzi", correct: true },
            { id: "c", text: "Kopiuj prompty z internetu", correct: false },
            { id: "d", text: "Pozwól AI samemu zdecydować jak odpowiedzieć", correct: false }
          ],
          shuffle: true,
          softHint: "Świadomość warstw = kontrola nad jakością.",
          successMessage: "Gratulacje! Potrafisz teraz budować prompty, które dają przewidywalnie doskonałe wyniki. To jest prawdziwy skill premium."
        }
      ]
    }
  ]
};

export default day8Content;
